import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../apiBase';

interface AgentStep {
    message: string;
    type: 'info' | 'process' | 'detail' | 'success' | 'warning' | 'error' | 'start' | 'done';
    timestamp: string;
}

interface AgentResult {
    baseStyle: string;
    finalSelection: Array<{
        item: string;
        type: 'real' | 'generated';
        product?: {
            name: string;
            description: string;
            imageUrl: string;
            link?: string;
        };
        description?: string;
        reason: string;
    }>;
    imagePrompt: string;
    generatedImage?: string;
    steps: AgentStep[];
}

const InteriorDesignAgent: React.FC = () => {
    const [styleInput, setStyleInput] = useState('');
    const [selectedPreset, setSelectedPreset] = useState('home-decor');
    const [customCategories, setCustomCategories] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [logs, setLogs] = useState<AgentStep[]>([]);
    const [result, setResult] = useState<AgentResult | null>(null);
    const logsEndRef = useRef<HTMLDivElement>(null);

    const presets = {
        'home-decor': { name: 'Home Decor', categories: ['lamp', 'candle holder', 'rug', 'throw pillow', 'mug'] },
        'living-room': { name: 'Living Room', categories: ['sofa', 'coffee table', 'floor lamp', 'decorative pillow', 'wall art'] },
        'bedroom': { name: 'Bedroom', categories: ['bedding', 'nightstand lamp', 'throw blanket', 'decorative pillow', 'candle'] },
        'kitchen': { name: 'Kitchen', categories: ['kitchen towel', 'cutting board', 'ceramic bowl', 'mug', 'storage jar'] },
        'bathroom': { name: 'Bathroom', categories: ['towel', 'soap dispenser', 'bath mat', 'candle', 'storage basket'] },
        'fashion': { name: 'Fashion', categories: ['t-shirt', 'jacket', 'jeans', 'sunglasses', 'bag'] },
        'office': { name: 'Office', categories: ['desk lamp', 'notebook', 'mug', 'plant pot', 'desk organizer'] },
        'custom': { name: 'Custom', categories: [] }
    };

    const scrollToBottom = () => {
        logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    const runAgent = async () => {
        if (!styleInput.trim()) return;

        setIsRunning(true);
        setResult(null);
        setLogs([{ message: 'Initializing Scene Generator...', type: 'start', timestamp: new Date().toISOString() }]);

        try {
            const thinkingInterval = setInterval(() => {
                setLogs(prev => {
                    if (prev[prev.length - 1].type === 'done') return prev;
                    return [...prev, { message: 'Agent is working...', type: 'process', timestamp: new Date().toISOString() }];
                });
            }, 3000);

            const categories = selectedPreset === 'custom'
                ? customCategories.split(',').map(c => c.trim()).filter(Boolean)
                : [];

            const response = await axios.post(`${API_BASE}/api/agent/interior-design`, {
                style: styleInput,
                preset: selectedPreset,
                customCategories: categories
            }, { withCredentials: true });

            clearInterval(thinkingInterval);

            if (response.data.success) {
                const data = response.data.data as AgentResult;
                // In a real streaming setup, we'd get logs one by one. 
                // Here we just replace the "thinking" logs with the actual detailed execution trace.
                setLogs(data.steps);
                setResult(data);
            } else {
                setLogs(prev => [...prev, { message: `Error: ${response.data.error}`, type: 'error', timestamp: new Date().toISOString() }]);
            }

        } catch (error: any) {
            console.error('Agent failed:', error);
            setLogs(prev => [...prev, { message: `Request failed: ${error.message}`, type: 'error', timestamp: new Date().toISOString() }]);
        } finally {
            setIsRunning(false);
        }
    };

    const getLogColor = (type: string) => {
        switch (type) {
            case 'start': return 'text-fuchsia-400 font-bold';
            case 'process': return 'text-blue-400';
            case 'success': return 'text-green-400 font-semibold';
            case 'warning': return 'text-yellow-400';
            case 'error': return 'text-red-400 font-bold';
            case 'detail': return 'text-slate-500 text-sm pl-4';
            case 'done': return 'text-pink-400 font-bold';
            default: return 'text-slate-300';
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8 p-4">
            <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-transparent bg-clip-text">
                    Interior Design Agent 🕵️‍♀️
                </h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                    An autonomous agent that brainstorms a room concept, searches your inventory for matching products,
                    and generates a cohesive visualization prompt.
                </p>
            </div>

            {/* Input Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-2xl mx-auto space-y-4">
                {/* Preset Selector */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Product Preset</label>
                    <select
                        value={selectedPreset}
                        onChange={(e) => setSelectedPreset(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        disabled={isRunning}
                    >
                        {Object.entries(presets).map(([key, preset]) => (
                            <option key={key} value={key}>{preset.name}</option>
                        ))}
                    </select>
                    {selectedPreset !== 'custom' && (
                        <p className="text-xs text-slate-500">
                            Will search for: {presets[selectedPreset as keyof typeof presets].categories.join(', ')}
                        </p>
                    )}
                </div>

                {/* Custom Categories Input */}
                {selectedPreset === 'custom' && (
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Custom Categories (comma-separated)</label>
                        <input
                            type="text"
                            value={customCategories}
                            onChange={(e) => setCustomCategories(e.target.value)}
                            placeholder="e.g. sofa, lamp, rug, plant, vase"
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            disabled={isRunning}
                        />
                    </div>
                )}

                {/* Style Input */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <input
                        type="text"
                        value={styleInput}
                        onChange={(e) => setStyleInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runAgent()}
                        placeholder="e.g. 'Scandinavian minimalist' or 'Cozy bohemian'"
                        className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                        disabled={isRunning}
                    />
                    <button
                        onClick={runAgent}
                        disabled={isRunning || !styleInput.trim()}
                        className={`px-6 py-3 rounded-lg font-semibold text-white transition-all transform active:scale-95 ${isRunning ? 'bg-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-indigo-500/30'
                            }`}
                    >
                        {isRunning ? 'Generating...' : '✨ Generate Scene'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Logs Terminal */}
                <div className="bg-slate-900 rounded-xl shadow-lg border border-slate-800 overflow-hidden flex flex-col h-[600px]">
                    <div className="bg-slate-800 px-4 py-2 flex items-center justify-between border-b border-slate-700">
                        <span className="text-xs font-mono text-slate-400">agent_execution_log.txt</span>
                        <div className="flex gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-500/50"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500/50"></div>
                        </div>
                    </div>
                    <div className="p-4 overflow-y-auto flex-1 font-mono text-sm space-y-1.5 custom-scrollbar">
                        {logs.length === 0 && (
                            <div className="text-slate-600 italic text-center mt-20">Waiting for instructions...</div>
                        )}
                        {logs.map((log, i) => (
                            <div key={i} className={`${getLogColor(log.type)} animate-in fade-in slide-in-from-left-1 duration-300`}>
                                <span className="opacity-30 mr-2 text-xs">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                                {log.message}
                            </div>
                        ))}
                        {isRunning && (
                            <div className="text-indigo-400 animate-pulse">▋</div>
                        )}
                        <div ref={logsEndRef} />
                    </div>
                </div>

                {/* Results Panel */}
                <div className="space-y-6 h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                    {result ? (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

                            {/* Vision Card */}
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-indigo-100">
                                <h3 className="text-sm uppercase tracking-wide text-indigo-500 font-bold mb-2">Design Vision</h3>
                                <p className="text-slate-700 leading-relaxed italic">"{result.baseStyle}"</p>
                            </div>

                            {/* Selected Products */}
                            <div className="space-y-3">
                                <h3 className="text-sm uppercase tracking-wide text-slate-500 font-bold">Curated Inputs ({result.finalSelection.length})</h3>
                                {result.finalSelection.map((item, i) => (
                                    <div key={i} className="group bg-white rounded-lg p-3 shadow-sm border border-slate-200 hover:border-indigo-300 transition flex gap-4">
                                        <div className="w-16 h-16 bg-slate-100 rounded-md overflow-hidden flex-shrink-0 border border-slate-100">
                                            {item.product?.imageUrl ? (
                                                <img src={item.product.imageUrl} alt={item.product?.name || item.item} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-xl">{item.type === 'generated' ? '🤖' : '📦'}</div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <h4 className="font-semibold text-slate-800 text-sm truncate pr-2">{item.product?.name || item.item}</h4>
                                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${item.type === 'generated' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                                                    {item.type === 'generated' ? 'AI Generated' : item.item}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.reason || item.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Generated Image */}
                            {result.generatedImage && (
                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
                                    <h3 className="text-sm uppercase tracking-wide text-indigo-600 font-bold mb-3">🎨 AI Generated Scene</h3>
                                    <img
                                        src={result.generatedImage}
                                        alt="AI Generated Interior Scene"
                                        className="w-full rounded-lg shadow-lg border border-white"
                                    />
                                </div>
                            )}

                            {/* Final Prompt */}
                            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-sm uppercase tracking-wide text-slate-500 font-bold">Scene Prompt</h3>
                                    <button
                                        onClick={() => copyToClipboard(result.imagePrompt)}
                                        className="text-xs text-indigo-600 hover:underline font-medium"
                                    >
                                        Copy
                                    </button>
                                </div>
                                <div className="bg-white p-3 rounded border border-slate-200 text-xs font-mono text-slate-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                                    {result.imagePrompt}
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                            <div className="text-center p-8">
                                <div className="text-4xl mb-4 opacity-50">🖼️</div>
                                <h3 className="text-slate-500 font-medium">Results will appear here</h3>
                                <p className="text-slate-400 text-sm mt-1">Run the agent to see the design process</p>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default InteriorDesignAgent;
